import { z } from 'zod'
import { recordingDetail, recordingSummary } from './api'
import { batchResult } from './api-v1'

const id = z.string().uuid()
export const moveBody = z.object({ studioId: id, abandonPendingUploads: z.boolean().optional() }).strict()
export const duplicateBody = z.object({ studioId: id, title: z.string().trim().min(1).max(200).optional() }).strict()
export const copyEditBody = z.object({
  fromRecordingId: id,
  map: z.record(z.string().min(1).max(200), z.string().min(1).max(200).nullable()).optional(),
  offsetMs: z.number().finite().int().min(-86400000).max(86400000).optional(), dryRun: z.boolean().optional(),
}).strict()
export const copyEditReport = z.object({
  mapped: z.array(z.object({ sourceId: z.string(), targetId: z.string() })),
  unmapped: z.array(z.object({ sourceId: z.string(), person: z.string(), kind: z.string(), reason: z.enum(['no_match', 'ambiguous', 'excluded']), boundaries: z.number() })),
  droppedBoundaries: z.number(), droppedMarkers: z.number(), aspectDiffers: z.boolean(),
})
export type CopyEditReport = z.infer<typeof copyEditReport>
export const copyEditResult = batchResult.extend({ report: copyEditReport })
export type CopyEditResult = z.infer<typeof copyEditResult>
export type CopyEditBody = z.infer<typeof copyEditBody>
export const transfer = z.object({
  id, kind: z.enum(['move', 'duplicate']), status: z.enum(['queued', 'running', 'done', 'failed']),
  sourceRecordingId: z.string(), targetRecordingId: z.string(), fromStudioId: z.string(), toStudioId: z.string(),
  fromTeamId: z.string(), toTeamId: z.string(), userId: z.string().nullable(), userName: z.string(), tokenId: z.string().nullable(),
  title: z.string().nullable(), bytesTotal: z.number(), bytesCopied: z.number(), error: z.string().nullable(),
  createdAt: z.string(), finishedAt: z.string().nullable(), targetRecording: recordingSummary.optional(),
})
export type Transfer = z.infer<typeof transfer>
export { recordingDetail as movedRecording }
export type TransferSource = { id: string; userId: string | null; displayName: string; sourceKey: string }

/** Identity requires both non-null user IDs, otherwise compare normalized names. Never guess ties. */
export function matchSources(source: TransferSource[], target: TransferSource[], overrides: Record<string, string | null> = {}) {
  const map: Record<string, string> = {}
  const unmapped: Omit<CopyEditReport['unmapped'][number], 'boundaries'>[] = []
  for (const s of source) {
    let reason: 'no_match' | 'ambiguous' | 'excluded' = 'no_match'
    const explicit = Object.hasOwn(overrides, s.id)
    const matches = explicit ? target.filter((t) => t.id === overrides[s.id])
      : target.filter((t) => t.sourceKey === s.sourceKey && (s.userId && t.userId ? s.userId === t.userId : s.displayName.normalize('NFKC').toLowerCase() === t.displayName.normalize('NFKC').toLowerCase()))
    if (explicit && overrides[s.id] === null) reason = 'excluded'
    else if (matches.length === 1) { map[s.id] = matches[0]!.id; continue }
    else if (matches.length > 1) reason = 'ambiguous'
    unmapped.push({ sourceId: s.id, person: s.displayName, kind: s.sourceKey, reason })
  }
  return { map, unmapped }
}

/** Remaps exact strings, object keys and escaped JSON-pointer path segments in batch ledgers. */
export function remapJson<T>(value: T, map: Record<string, string>): T {
  const rewrite = (s: string): string => {
    if (Object.hasOwn(map, s)) return map[s]!
    if (!s.startsWith('/')) return s
    return '/' + s.slice(1).split('/').map((p) => {
      const raw = p.replace(/~1/g, '/').replace(/~0/g, '~')
      const next = Object.hasOwn(map, raw) ? map[raw]! : raw
      return next.replace(/~/g, '~0').replace(/\//g, '~1')
    }).join('/')
  }
  if (typeof value === 'string') return rewrite(value) as T
  if (Array.isArray(value)) return value.map((v) => remapJson(v, map)) as T
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [rewrite(k), remapJson(v, map)])) as T
  return value
}
